import {first} from 'lodash';

import {ExtendedError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {ApiChannel, ChannelModel} from '../../../models/api/channelModel';
import {ApiChannelDataGroup, DataGroupModel} from '../../../models/api/dataGroupModel';
import {fetchApiAsync, putApiAsync} from '../../../services/http/http';
import {HttpNetworkError, HttpTimeoutError} from '../../../services/http/httpError';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {getCompanyUrl} from '../company/companySelectors';
import {updateDataAction} from '../dataGroupsActions';
import {getChannels} from './channelsSelectors';

/*
 * Action types.
 */

export enum ChannelsActionsEnum {
  CHANNEL_DELETED = '@@data/channels/CHANNEL_DELETED',
}

/*
 * Types.
 */

export interface ChannelDeletedAction extends ActionWithPayload<number> {
  type: ChannelsActionsEnum.CHANNEL_DELETED;
}

/*
 * Action creators.
 */

export function deleteChannel(channelId: number): ThunkAction {
  return (dispatch, getState) => {
    // If we don't have this channel, nothing to do.
    const existingChannel = getState().data.channels.byId[channelId];
    if (!existingChannel) {
      return null;
    }

    const action: ChannelDeletedAction = {
      type: ChannelsActionsEnum.CHANNEL_DELETED,
      payload: channelId,
    };
    return dispatch(action);
  };
}

export function fetchChannelByIdAsyncAction(channelId: number): ThunkAsyncAction<ChannelModel | undefined> {
  return async (dispatch, getState) => {
    const state = getState();
    const companyUrl = getCompanyUrl(state);
    const channels = getChannels(state);

    const channel = channels[channelId];
    if (channel) {
      return channel;
    }

    try {
      const result = await fetchApiAsync(ApiChannelDataGroup, `${companyUrl}/channels/${channelId}`);
      dispatch(updateDataAction(result));

      return findChannelInDataGroup(result);
    } catch (error) {
      if (!(error instanceof HttpNetworkError || error instanceof HttpTimeoutError)) {
        reportToBugsnag(new ExtendedError('Failed to fetch channel', error));
      }

      return undefined;
    }
  };
}

export function updateChannelAsyncAction(channel: ChannelModel): ThunkAsyncAction<ChannelModel> {
  return async (dispatch) => {
    try {
      const newChannel = await putApiAsync(ApiChannel, channel.url, ApiChannel.serialize(channel));
      dispatch(updateDataAction({channels: [newChannel]}));
      return newChannel;
    } catch (error) {
      if (!(error instanceof HttpNetworkError || error instanceof HttpTimeoutError)) {
        reportToBugsnag(new ExtendedError('Failed to update channel', error));
      }
      throw error;
    }
  };
}

/*
 * Helpers.
 */

function findChannelInDataGroup(dataGroup: DataGroupModel) {
  return first(dataGroup.channels || []);
}

/*
 * Base actions.
 */

export function dismissChannelCanArchiveActionAsyncAction(channel: ChannelModel): ThunkAsyncAction {
  return async (dispatch) => {
    const {url} = channel;
    const body = {settings: {displayMassArchiveBanner: false}};

    const result = await putApiAsync(ApiChannelDataGroup, url, body);
    dispatch(updateDataAction(result));
  };
}

/*
 * Union type.
 */

export type ChannelAction = ChannelDeletedAction;
